import { purchaseReturnApi } from '../../api';

import { CustomResponse } from '../../types/api/CustomResponse';

import {
  PurchaseReturnDocumentCandidate,
  PurchaseReturnEligibility,
  PurchaseReturnPreview,
  PurchaseReturnRequest,
  PurchaseSpringPage,
} from '../../types/fiscal/PurchaseReturnEvent';

import { FiscalEventSubmission } from '../../types/fiscal/FiscalEvent';

// =====================================================
// 📦 Opciones del borrador
// =====================================================

export interface PurchaseReturnDraftOptions {
  ambiente?: '00' | '01';
}

export interface PurchaseReturnSendOptions {
  ambiente?: '00' | '01';
  idEnvio?: number;
}

// =====================================================
// 🔌 Service
// =====================================================

const PurchaseReturnEventService = {
  /**
   * Busca compras de Sujeto Excluido que pueden participar
   * en un Evento de Retorno. `supplierId`/`startDate`/`endDate` escopan
   * la búsqueda del lado servidor (p. ej. al agregar más documentos a un
   * evento que ya tiene un proveedor seleccionado).
   *
   * GET /api/dte/purchases/return/documents
   */
  searchDocuments: async (
    query = '',
    page = 0,
    size = 10,
    filters: { supplierId?: number; startDate?: string; endDate?: string } = {}
  ): Promise<CustomResponse<PurchaseSpringPage<PurchaseReturnDocumentCandidate>>> => {
    const params = new URLSearchParams({
      query,
      page: String(page),
      size: String(Math.min(size, 20)),
    });
    if (filters.supplierId != null) params.set('supplierId', String(filters.supplierId));
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);

    const response = await purchaseReturnApi.fetchCustom(`/documents?${params.toString()}`);

    return response.data;
  },

  /**
   * Consulta si una compra es elegible para
   * participar en un Evento de Retorno.
   *
   * GET /api/dte/purchases/return/eligibility/{purchaseId}
   */
  eligibility: async (
    purchaseId: number
  ): Promise<CustomResponse<PurchaseReturnEligibility>> => {
    const response = await purchaseReturnApi.fetchCustom(`/eligibility/${purchaseId}`);

    return response.data;
  },

  /**
   * Genera la previsualización fiscal
   * antes de guardar el borrador.
   *
   * POST /api/dte/purchases/return/preview
   */
  preview: async (
    request: PurchaseReturnRequest
  ): Promise<CustomResponse<PurchaseReturnPreview>> => {
    const response = await purchaseReturnApi.addCustom('/preview', request);

    return response.data;
  },

  /**
   * Guarda el Evento de Retorno como borrador (DRAFT).
   * No valida contra schema, no firma ni transmite.
   *
   * POST /api/dte/purchases/return/draft
   */
  saveDraft: async (
    request: PurchaseReturnRequest,
    idempotencyKey: string,
    options: PurchaseReturnDraftOptions = {}
  ): Promise<CustomResponse<FiscalEventSubmission>> => {
    const { ambiente = '00' } = options;

    const params = new URLSearchParams({ ambiente });

    const response = await purchaseReturnApi.addCustomWithHeaders(
      `/draft?${params.toString()}`,
      request,
      { 'Idempotency-Key': idempotencyKey }
    );

    return response.data;
  },

  /**
   * Valida el JSON ya generado contra el schema oficial fe-eret-v1.
   * Transiciona DRAFT -> VALIDATED.
   *
   * POST /api/dte/purchases/return/{id}/validate
   */
  validate: async (id: number): Promise<CustomResponse<FiscalEventSubmission>> => {
    const response = await purchaseReturnApi.addWithoutData(`/${id}/validate`);

    return response.data;
  },

  /**
   * Firma el JSON ya validado. Transiciona VALIDATED -> SIGNED.
   *
   * POST /api/dte/purchases/return/{id}/sign
   */
  sign: async (id: number): Promise<CustomResponse<FiscalEventSubmission>> => {
    const response = await purchaseReturnApi.addWithoutData(`/${id}/sign`);

    return response.data;
  },

  /**
   * Transmite el JSON ya firmado a Hacienda. Único paso que transmite.
   *
   * POST /api/dte/purchases/return/{id}/send
   */
  send: async (
    id: number,
    options: PurchaseReturnSendOptions = {}
  ): Promise<CustomResponse<FiscalEventSubmission>> => {
    const { ambiente = '00', idEnvio = 1 } = options;

    const params = new URLSearchParams({ ambiente, idEnvio: String(idEnvio) });

    const response = await purchaseReturnApi.addWithoutData(`/${id}/send?${params.toString()}`);

    return response.data;
  },
};

export default PurchaseReturnEventService;
